import { type CSSProperties, useRef } from "react";

/** Delay (ms) before a row's entrance starts; `null` = no entrance (the row is simply there). */
export type Entrance = number | null;

/** Opening a conversation staggers only this many of the NEWEST rows... */
export const STAGGER_ROWS = 10;
/** ...this far apart (llm-console: one by one, total well under half a second of stagger). */
export const STAGGER_MS = 35;

/**
 * Decide each row's entrance once, the first time the row is seen:
 *  - the first batch (the conversation opening, or a different thread swapping in): the last
 *    `STAGGER_ROWS` rows rise in one by one, earlier rows are static;
 *  - a row that shows up later at the end (a live message): enters at once;
 *  - a row that shows up above the rows already known (pagination prepend): static.
 * Known rows keep their first answer, so a re-render never replays anything. Returns `known`
 * itself when nothing changed (stable identity for memoised rows).
 */
export function classifyEntrances(
  known: ReadonlyMap<string, Entrance>,
  keys: readonly string[],
): ReadonlyMap<string, Entrance> {
  if (keys.length === 0) return known;
  const firstKnown = keys.findIndex((k) => known.has(k));
  if (firstKnown === -1) {
    const out = new Map<string, Entrance>();
    const from = Math.max(0, keys.length - STAGGER_ROWS);
    keys.forEach((k, i) => {
      out.set(k, i < from ? null : (i - from) * STAGGER_MS);
    });
    return out;
  }
  let out: Map<string, Entrance> | null = null;
  keys.forEach((k, i) => {
    if (known.has(k)) return;
    out ??= new Map(known);
    out.set(k, i > firstKnown ? 0 : null);
  });
  return out ?? known;
}

/** The entrance of each message row, decided on first sight (render-phase ref: idempotent). */
export function useRowEntrances(keys: readonly string[]): ReadonlyMap<string, Entrance> {
  const ref = useRef<ReadonlyMap<string, Entrance>>(new Map());
  ref.current = classifyEntrances(ref.current, keys);
  return ref.current;
}

/** The CSS class that plays llm-console's gentle-in (see `globals.css`). */
export const GENTLE_IN_CLASS = "wx-gentle-in";

/** className + style for a row's entrance: nothing for a static row, the delay as a CSS variable. */
export function entranceAttrs(enter: Entrance | undefined): {
  className?: string;
  style?: CSSProperties;
} {
  if (enter === null || enter === undefined) return {};
  return {
    className: GENTLE_IN_CLASS,
    style: enter > 0 ? ({ "--wx-enter-delay": `${enter}ms` } as CSSProperties) : undefined,
  };
}
